import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trophy, Clock, Users, MapPin, CheckCircle2, Crown, Star } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * GameSummaryDialog
 * Shown when an admin/creator clicks "Chiudi partita" on a TableCard.
 * Lets them record:
 *  - Winner (optional, from registered players)
 *  - Per-player scores (optional)
 *  - Notes about the session
 * Then saves to game_history and optionally deletes the table.
 */
/**
 * Props:
 *   table           – table object (game_name, categories, game_mode, venue, game_date, play_time, explanation_time, players, ...)
 *   open / onOpenChange / onSaved – dialog lifecycle
 *   initialWinnerId  – pre-selected winner player id (or "none")
 *   initialScores    – { [playerId]: { player_id, player_name, score } } – pre-filled from scoreboard
 */
export default function GameSummaryDialog({
  table,
  open,
  onOpenChange,
  onSaved,
  initialWinnerId = "none",
  initialScores = {},
}) {
  const [winnerId, setWinnerId] = useState(initialWinnerId);
  const [scores, setScores] = useState(initialScores);
  const [notes, setNotes] = useState(table?.notes || "");
  const [deleteAfter, setDeleteAfter] = useState(true);
  const [busy, setBusy] = useState(false);

  // Sync pre-filled values each time the dialog opens
  useEffect(() => {
    if (open) {
      setWinnerId(initialWinnerId);
      setScores(initialScores);
      setNotes(table?.notes || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!table) return null;

  const registeredPlayers = table.players || [];
  const playedAt = table.game_date || new Date().toISOString().slice(0, 10);
  const totalMinutes = (table.explanation_time || 0) + (table.play_time || 0);

  const handleScoreChange = (playerId, playerName, value) => {
    setScores((prev) => ({
      ...prev,
      [playerId]: { player_id: playerId, player_name: playerName, score: value === "" ? null : parseFloat(value) },
    }));
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      const winner = winnerId && winnerId !== "none"
        ? registeredPlayers.find((p) => p.id === winnerId)
        : null;

      const scoresList = Object.values(scores).filter((s) => s.score !== null && s.score !== undefined);

      const payload = {
        game_name: table.game_name,
        categories: table.categories || [],
        game_mode: table.game_mode || "Competitivo",
        venue: table.venue,
        played_at: playedAt,
        play_time: table.play_time,
        explanation_time: table.explanation_time || 0,
        players: registeredPlayers,
        extra_players: table.extra_players || 0,
        winner: winner ? { id: winner.id, name: winner.name } : null,
        scores: scoresList.length > 0 ? scoresList : null,
        notes: notes.trim(),
      };

      await api.post("/admin/history", payload);

      if (deleteAfter) {
        try {
          await api.delete(`/tables/${table.id}`);
        } catch (_) {
          // table may already be gone
        }
      }

      toast.success("Partita salvata nello storico!");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const formatTime = (mins) => {
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#FDFBF7] dark:bg-[#111111] border-[#EAE3D9] dark:border-[#2A2A2A]">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#2D2A26] dark:text-[#F5F1EA] flex items-center gap-2">
            <Trophy size={22} className="text-[#8A6B1F]" />
            Fine partita
          </DialogTitle>
          <DialogDescription className="text-[#757169] dark:text-[#B8B2AA]">
            Salva il riepilogo nello storico annuale
          </DialogDescription>
        </DialogHeader>

        {/* Game summary */}
        <div className="rounded-xl bg-white dark:bg-[#1A1A1A] border border-[#EAE3D9] dark:border-[#2A2A2A] p-4 space-y-3">
          <h3 className="font-display text-xl font-bold text-[#2D2A26] dark:text-[#F5F1EA]">
            {table.game_name}
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {(table.categories || []).map((cat) => (
              <Badge key={cat} variant="secondary" className="text-xs">{cat}</Badge>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm text-[#4A4742] dark:text-[#C8C2BA]">
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-[#2B593F] dark:text-[#8FC79F]" />
              <span>{table.venue}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-[#2B593F] dark:text-[#8FC79F]" />
              <span>{formatTime(table.play_time)} di gioco</span>
            </div>
            <div className="flex items-center gap-2">
              <Users size={14} className="text-[#2B593F] dark:text-[#8FC79F]" />
              <span>{registeredPlayers.length + (table.extra_players || 0)} giocatori</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-[#2B593F] dark:text-[#8FC79F]" />
              <span>{playedAt}</span>
            </div>
          </div>
          {registeredPlayers.length > 0 && (
            <div className="text-xs text-[#757169] dark:text-[#B8B2AA]">
              Giocatori: {registeredPlayers.map((p) => p.name).join(", ")}
              {(table.extra_players || 0) > 0 && ` + ${table.extra_players} ospiti`}
            </div>
          )}
        </div>

        {/* Winner */}
        {registeredPlayers.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm font-medium text-[#2D2A26] dark:text-[#F5F1EA] flex items-center gap-2">
              <Crown size={14} className="text-[#8A6B1F]" />
              Vincitore / Vincitrice (opzionale)
            </Label>
            <Select value={winnerId} onValueChange={setWinnerId}>
              <SelectTrigger className="bg-white dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA]">
                <SelectValue placeholder="Seleziona vincitore..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessun vincitore / Collaborativo</SelectItem>
                {registeredPlayers.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Scores */}
        {registeredPlayers.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm font-medium text-[#2D2A26] dark:text-[#F5F1EA] flex items-center gap-2">
              <Star size={14} className="text-[#8A6B1F]" />
              Punteggi (opzionali)
            </Label>
            <div className="space-y-2">
              {registeredPlayers.map((p) => (
                <div key={p.id} className="flex items-center gap-3">
                  <span className="text-sm text-[#4A4742] dark:text-[#C8C2BA] w-32 truncate">{p.name}</span>
                  <Input
                    type="number"
                    placeholder="punteggio"
                    className="flex-1 h-8 text-sm bg-white dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] text-[#2D2A26] dark:text-[#F5F1EA]"
                    value={scores[p.id]?.score ?? ""}
                    onChange={(e) => handleScoreChange(p.id, p.name, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-[#2D2A26] dark:text-[#F5F1EA]">
            Note sulla serata (opzionale)
          </Label>
          <textarea
            className="w-full min-h-[70px] rounded-md border border-[#EAE3D9] dark:border-[#2A2A2A] bg-white dark:bg-[#1A1A1A] text-sm text-[#2D2A26] dark:text-[#F5F1EA] p-2.5 resize-none focus:outline-none focus:ring-1 focus:ring-[#2B593F]"
            maxLength={1000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Partita memorabile, momenti salienti..."
          />
        </div>

        {/* Delete after */}
        <div className="flex items-center gap-2">
          <input
            id="delete-after"
            type="checkbox"
            checked={deleteAfter}
            onChange={(e) => setDeleteAfter(e.target.checked)}
            className="h-4 w-4 rounded border-[#EAE3D9] accent-[#2B593F]"
          />
          <label htmlFor="delete-after" className="text-sm text-[#4A4742] dark:text-[#C8C2BA] cursor-pointer">
            Elimina il tavolo dopo il salvataggio
          </label>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="border-[#EAE3D9] dark:border-[#4A4742] dark:text-[#C8C2BA] dark:bg-transparent dark:hover:bg-[#2A2A2A]"
          >
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            disabled={busy}
            className="bg-[#2B593F] hover:bg-[#234830] text-white"
          >
            {busy ? "Salvataggio..." : "Salva nello storico"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
